import { C1_EloItem_Type, type C1_EloItem_Data, type C1_EloItem_Definition } from "../types/C1_EloTypes";
import { createEloItemInstance } from "./C1_ensureEloEquipmentDefaults";
import C1_EloItem, { migrateLegacyEloItemTreeNode } from "./C1_EloItem";
import { C1_EloGroup } from "./C1_EloGroup";
import { C1_Button } from "./C1_Button";
import { nextRepeatedItemDefinitions } from "./C1_eloTreeEditing";
import { c1_state } from "./C1_runtimeContext";
import { availableFurnitureEloDefinitions } from "./C1_furnitureEloOptions";
import type { C1_Furniture } from "./C1_Furniture";

export type C1_EloGhostAction =
  | { type: "append"; parentItemId: string; definition: C1_EloItem_Definition }
  | { type: "append-repeat"; groupItemId: string; definition: C1_EloItem_Definition };

export type C1_EloGhostItem = {
  item: C1_EloItem;
  action: C1_EloGhostAction;
  isActionRoot: boolean;
};

function definitionFor(item: C1_EloItem): C1_EloItem_Definition | undefined {
  return item.definition?.toDefinition();
}

/** Keeps preview identity and ancestry separate from the persistent owner. */
class GhostGroup extends C1_EloGroup {
  constructor(private readonly source: C1_EloItem_Data, private readonly previewParent: C1_EloGroup) {
    super(migrateLegacyEloItemTreeNode(source, previewParent.owner!, previewParent._defId), previewParent.owner!);
    this.setTemporaryItems([]);
  }

  override get id(): string { return this.source.id; }
  override get parent(): C1_EloGroup { return this.previewParent; }
}

/** Supplies the normal button renderer with defaults without creating a real button. */
class GhostButton extends C1_Button {
  constructor(private readonly source: C1_EloItem_Data, private readonly previewParent: C1_EloGroup) {
    super(migrateLegacyEloItemTreeNode(source, previewParent.owner!, previewParent._defId), previewParent.owner!);
  }

  override get id(): string { return this.source.id; }
  override get parent(): C1_EloGroup { return this.previewParent; }
}

/** Reconstructs every preview descendant so layout and design rendering see the complete tree. */
function createGhostTree(data: C1_EloItem_Data, parent: C1_EloGroup): C1_EloItem {
  const group = new GhostGroup(data, parent);
  if (group.definition?.type === C1_EloItem_Type.Button) return new GhostButton(data, parent);
  const children = data.items.map((child) => createGhostTree(child, group));
  group.setTemporaryItems(children);
  return group;
}

function deterministicGhostIds(data: C1_EloItem_Data, prefix: string): C1_EloItem_Data {
  return {
    ...data,
    id: prefix,
    items: data.items.map((child, index) => deterministicGhostIds(child, `${prefix}-${index}`)),
  };
}

function fullyEnabledGhostData(
  definition: C1_EloItem_Definition,
  parentId: string,
): C1_EloItem_Data {
  const initial = createEloItemInstance(
    definition,
    c1_state.project.equipmentStandard,
    c1_state.settings.buttonDefaults,
  );
  const isRepeatGroup =
    (definition.type === C1_EloItem_Type.Group || definition.type === C1_EloItem_Type.PlugGroup) &&
    definition.details?.repeatCount !== undefined;
  const data = isRepeatGroup
    ? initial
    : {
        ...initial,
        items: (definition.items ?? []).map((child) => fullyEnabledGhostData(child, parentId)),
      };
  return deterministicGhostIds(data, `ghost-${parentId}-${definition.id}`);
}

export function createEloGhostItems(
  root: C1_EloGroup,
  origin: { x: number; y: number },
  rotation: number,
  reflect: boolean,
  placementProps: Record<string, number>,
  rootDefinitions?: C1_EloItem_Definition[],
  technicalMode = false,
): C1_EloGhostItem[] {
  const result: C1_EloGhostItem[] = [];
  const owner = root.owner?.c1;
  const furnitureProperties = owner?.className === "C1_Furniture" ? (owner as C1_Furniture).baseProps : {};
  const wallDocked = owner?.className === "C1_Furniture" && (owner as C1_Furniture).wallDocked !== undefined;
  const injections: Array<{
    parent: C1_EloGroup;
    ghosts: Array<{ root: C1_EloItem; action: C1_EloGhostAction }>;
  }> = [];

  // Capture the real tree before injecting anything, so projections nested in
  // other projections are not interpreted as additional model definitions.
  for (const parent of root.allItems) {
    if (!(parent instanceof C1_EloGroup)) continue;
    let ancestor: C1_EloItem | undefined = parent;
    let available = true;
    while (ancestor) {
      const requirement = definitionFor(ancestor)?.requiresFurnitureProperty;
      if (requirement && furnitureProperties[requirement] !== true) available = false;
      if (definitionFor(ancestor)?.onlyIfWallDocked && !wallDocked) available = false;
      if (ancestor === root) break;
      ancestor = ancestor.parent instanceof C1_EloGroup ? ancestor.parent : undefined;
    }
    if (!available) continue;
    const registeredDefinition = definitionFor(parent);
    const definition =
      parent === root && rootDefinitions
        ? { ...registeredDefinition, type: C1_EloItem_Type.Group, items: rootDefinitions } as C1_EloItem_Definition
        : registeredDefinition;
    if (!definition?.items?.length) continue;

    const repeatGroup =
      (definition.type === C1_EloItem_Type.Group || definition.type === C1_EloItem_Type.PlugGroup) &&
      definition.details?.repeatCount !== undefined;
    // Only design mode uses the compact strip's own add slot. Technical mode
    // needs a normal tag projection for the next outlet in the group.
    if (!technicalMode && repeatGroup && definition.type === C1_EloItem_Type.PlugGroup) continue;
    const missingDefinitions = repeatGroup
      ? nextRepeatedItemDefinitions(definition, parent.toLegacyTreeData())
      : definition.items.filter(
          (childDefinition) =>
            !parent.toLegacyTreeData().items.some((child) => child.definitionId === childDefinition.id),
        );

    const offeredDefinitions = availableFurnitureEloDefinitions(missingDefinitions, furnitureProperties, wallDocked).flatMap(definition =>
      definition.requiresFurnitureProperty
        ? (definition.items ?? []).map(child => ({
            ...definition,
            // Adding one electrical child also creates its structural container.
            items: [{ ...child, defaultEnabled: true }],
          }))
        : [definition],
    );
    const ghosts = offeredDefinitions.map((missingDefinition) => {
      const ghostData = fullyEnabledGhostData(missingDefinition, parent.id);
      if (missingDefinition.requiresFurnitureProperty) {
        ghostData.id += `-${missingDefinition.items![0].id}`;
      }
      const ghost = createGhostTree(ghostData, parent);
      const action: C1_EloGhostAction = repeatGroup
        ? { type: "append-repeat", groupItemId: parent.id, definition }
        : { type: "append", parentItemId: parent.id, definition: missingDefinition };
      return { root: ghost, action };
    });
    if (!ghosts.length) continue;
    const originalItems = parent.items;
    injections.push({ parent, ghosts });
    parent.setTemporaryItems([...originalItems, ...ghosts.map((entry) => entry.root)]);
  }

  try {
    // Evaluate the complete future sibling layout once. Existing items are
    // deliberately included: formulas using ItemIndex/ItemCount must move them
    // exactly as they would move after the projected items are persisted.
    root.evaluateAbsolutePlacements(origin, rotation, reflect, placementProps);

    for (const injection of injections) {
      for (const ghost of injection.ghosts) {
        const ghostItems = ghost.root instanceof C1_EloGroup ? ghost.root.allItems : [ghost.root];
        for (const item of ghostItems) {
          if (definitionFor(item)?.requiresFurnitureProperty) continue;
          result.push({ item, action: ghost.action, isActionRoot: item === ghost.root });
        }
      }
    }
  } finally {
    // Preview facets never enter owner collections or the global registry.
    // Restore the canonical child arrays after evaluating their future layout.
    for (const injection of injections) {
      injection.parent.setTemporaryItems(undefined);
    }
  }

  return result;
}
